// Federal Generator Status Thresholds (40 CFR 260.10)
//
// The tiers are defined in kilograms. The regulation prints rounded pounds
// beside them (220 lb is 99.79 kg, not 100 kg), so there are no pound
// constants: classify mass with the comparisons at the bottom of this file, and
// show a threshold in pounds with `thresholdPounds` / `formatThreshold*`.
export const LQG_THRESHOLD_ACUTE_HAZARDOUS_KG = 1;

export const VSQG_THRESHOLD_HAZARDOUS_KG = 0;

export const SQG_THRESHOLD_HAZARDOUS_KG = 100;

export const LQG_THRESHOLD_HAZARDOUS_KG = 1000;

// Kansas Small Quantity Generator (K.A.R. 28-31-4) — 25 kg, printed as 55 lb
export const KSQG_THRESHOLD_HAZARDOUS_KG = 25;
// Texas Class 1 Only Threshold — 100 kg, printed as 220 lb
export const TEXAS_CLASS_1_ONLY_KG = 100;

// (40 CFR 262.14/262.16)
// max on-site at any time (distinct from monthly generation)
export const VSQG_ACCUMULATION_THRESHOLD_KG = 1000;
export const VSQG_ACCUMULATION_THRESHOLD_ACUTE_KG = 1;
export const SQG_ACCUMULATION_THRESHOLD_KG = 6000;

// (Minn. R. 7045.0292)
// max on-site at any time (distinct from monthly generation)
export const MN_SQG_ACCUMULATION_THRESHOLD_KG = 3000;
export const MN_VSQG_ACCUMULATION_THRESHOLD_KG = 1000;
export const MN_ACCUMULATION_THRESHOLD_ACUTE_KG = 1;

// (RCSA § 22a-449(c)-102, modifying 40 CFR 262.34(d) as of July 1, 2000)
// max on-site at any time (distinct from monthly generation)
export const CT_SQG_ACCUMULATION_THRESHOLD_KG = 1000;

/** Exact by definition: the international pound is 0.45359237 kg. */
const KILOGRAMS_PER_POUND = 0.45359237;

/**
 * Slack allowed on a threshold comparison, in kilograms. A month recorded as a
 * round 100 kg arrives as a float sum that can land either side of the
 * threshold by a few ulps — and on the pound-denominated calculators only after
 * a kg → lb → kg round trip. A milligram of slack keeps that dust from deciding
 * a tier; no real determination turns on it.
 */
const THRESHOLD_TOLERANCE_KG = 1e-6;

/** Mass is strictly above the threshold — for rules phrased "more than N kg". */
export function kilogramsExceed(massKg: number, thresholdKg: number): boolean {
  return massKg > thresholdKg + THRESHOLD_TOLERANCE_KG;
}

/** Mass is at or above the threshold — for rules phrased "N kg or more". */
export function kilogramsReach(massKg: number, thresholdKg: number): boolean {
  return massKg > thresholdKg - THRESHOLD_TOLERANCE_KG;
}

/** {@link kilogramsExceed} for a mass measured in pounds. */
export function poundsExceed(massLb: number, thresholdKg: number): boolean {
  return kilogramsExceed(massLb * KILOGRAMS_PER_POUND, thresholdKg);
}

/** {@link kilogramsReach} for a mass measured in pounds. */
export function poundsReach(massLb: number, thresholdKg: number): boolean {
  return kilogramsReach(massLb * KILOGRAMS_PER_POUND, thresholdKg);
}

/** Pounds as generator status screens show them: grouped, at most one decimal. */
export function formatPounds(pounds: number): string {
  return pounds.toLocaleString("en-US", { maximumFractionDigits: 1 });
}

/** A kg threshold in pounds, unrounded — where it falls on a pound-denominated chart. */
export function thresholdPounds(thresholdKg: number): number {
  return thresholdKg / KILOGRAMS_PER_POUND;
}

/** A kg threshold in pounds, rounded like the waste totals it's read against. */
export function formatThresholdPounds(thresholdKg: number): string {
  return formatPounds(thresholdPounds(thresholdKg));
}

/** A kg threshold as the rule states it, pounds alongside: "1,000 kg (2,204.6 lb)". */
export function formatThreshold(thresholdKg: number): string {
  return `${thresholdKg.toLocaleString("en-US")} kg (${formatThresholdPounds(thresholdKg)} lb)`;
}
